import { validateId } from '~/repositories/utils';
import { envConfig } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type {
  BalanceSheetDto,
  CreateBalanceSheetParams,
  CreateRentPaymentsReportParams,
  GeneralLedgerDto,
  RentPaymentsReportDto,
  ReportSectionDto,
  ReportType,
} from '~/swagger/accounting/Api';
import type {
  AccountsPayableReportDto,
  BankAccountActivityReportDto,
  DelinquencyReportDto,
  GetOwnerStatementReportParams,
  LeaseMagicTagsReportDto,
  LeaseUnpaidLedgerReportDto,
  OwnerStatementReportDto,
  PropertyReserveReportDto,
  TenantDirectoryReportDto,
} from '~/swagger/portal/Api';
const REPORTS_URL = '/api/reports';
const ACCOUNTING_REPORTS_URL = `${envConfig.service.accounting}/company-portal/reports`;

type DateRange = { start: string; end?: string } | { start?: string; end: string };

export type GeneralLedgerReportFilter = {
  basis: string;
  reportType: string;
  propertyIds?: string[];
  startDate: string;
  endDate: string;
  coaIds?: string[];
};

export type IncomeStatementReportFilter = {
  dateRange?: DateRange;
  portfolioIds?: string[];
  propertyIds?: string[];
  includeNoPropertyBills?: boolean;
  companyBills?: boolean;
  ownerIds?: string[];
  accountingBasis?: string;
};

export type TenantDirectoryReportFilter = {
  portfolioIds?: string[];
  propertyIds?: string[];
  includeNoActiveLease?: boolean;
  ownerIds?: string[];
};

export type RentRollReportFilter = {
  portfolioIds?: string[];
  propertyIds?: string[];
  includeEmptyUnits?: 'true' | 'false';
  includeEmptyProperties?: 'true' | 'false';
  ownerIds?: string[];
};

export type BankAccountActivityReportFilter = {
  dateRange?: DateRange;
  bankAccountId: string;
};

export type DelinquencyReportFilter = {
  portfolioIds?: string[];
  propertyIds?: string[];
  MinimumDue?: string;
  IncludeEmptyProperties?: boolean;
  ownerIds?: string[];
};

export type RentalInsuranceReportFilter = {
  portfolioIds?: string[];
  propertyIds?: string[];
  ownerIds?: string[];
};

export type PropertyReserveReportFilter = {
  portfolioIds?: string[];
  propertyIds?: string[];
  ownerIds?: string[];
};

export type AccountsPayableReportFilter = {
  includeOverdue?: boolean;
  groupBy?: string;
  companyBills?: boolean;
  asOfDate?: string;
};

export type PrepaidRentReportFilter = {
  portfolioIds?: string[];
  propertyIds?: string[];
  ownerIds?: string[];
  includeEndedLeases?: boolean;
};

export type PrepaidRentReportTenantDto = {
  id: string;
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  moveInDate?: string;
  moveOutDate?: string | null;
};

export type PrepaidRentReportItemDto = {
  lease: any;
  unit: any;
  tenants: PrepaidRentReportTenantDto[];
};

export type PrepaidRentReportGroupDto = {
  portfolio: any;
  property: any;
  items: PrepaidRentReportItemDto[];
};

export type PrepaidRentReportMetadataDto = {
  totalLeases: number;
  totalPrepaidAmount: number;
};

export type PrepaidRentReportDto = {
  groups: PrepaidRentReportGroupDto[];
  metadata: PrepaidRentReportMetadataDto;
};

export type LateFeesReportFilter = {
  'dateRange.start'?: string;
  'dateRange.end'?: string;
  portfolioIds?: string[];
  propertyIds?: string[];
  ownerIds?: string[];
  includePaid?: boolean;
  includeManualCharges?: boolean;
};

export type LateFeeEntryDto = {
  id: string;
  source: 'System' | 'Manual';
  billDate: string;
  dueDate: string;
  totalAmount: number;
  allocatedAmount: number;
  dueAmount: number;
  memo?: string;
};

export type LateFeesReportTenantDto = {
  id: string;
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  moveInDate?: string;
  moveOutDate?: string | null;
};

export type LateFeesReportItemDto = {
  lease: any;
  unit: any;
  tenants: LateFeesReportTenantDto[];
  lateFees: LateFeeEntryDto[];
};

export type LateFeesReportGroupDto = {
  portfolio: any;
  property: any;
  items: LateFeesReportItemDto[];
};

export type LateFeesReportMetadataDto = {
  totalLateFees: number;
  totalAmount: number;
  totalDueAmount: number;
  totalAllocatedAmount: number;
};

export type LateFeesReportDto = {
  groups: LateFeesReportGroupDto[];
  metadata: LateFeesReportMetadataDto;
};

export enum CashFlowGroupBy {
  Total = 'total',
  Property = 'property',
  Year = 'year',
  Quarter = 'quarter',
  Month = 'month',
}

export type CreateCashFlowStatementRequestDto = {
  reportType: ReportType;
  groupBy: CashFlowGroupBy;
  portfolioIds?: string[];
  propertyIds?: string[];
  startDate: string;
  endDate: string;
};

export type CashFlowStatementDto = {
  title: string;
  operatingRevenue: ReportSectionDto;
  operatingExpenses: ReportSectionDto;
  operatingDeposits: ReportSectionDto;
  netCashFromOperations: number;
  nonOperatingRevenue: ReportSectionDto;
  nonOperatingExpenses: ReportSectionDto;
  netNonOperatingIncome: number;
  investingActivities: ReportSectionDto;
  netCashFromInvesting: number;
  financingActivities: ReportSectionDto;
  netCashFromFinancing: number;
  netChangeInCash: number;
  beginningCashBalance: number;
  endingCashBalance: number;
};

export type LeaseMagicTagsReportFilter = {
  // Property filters
  propertyIds?: string[];
  portfolioIds?: string[];
  unitIds?: string[];
  // Tenant filters
  tenantIds?: string[];
  // Lease filters
  leaseIds?: string[];
  ended?: boolean;
  monthToMonth?: boolean;
  eviction?: boolean;
  draft?: boolean;
  // Date range filters
  'leaseStart.start'?: string;
  'leaseStart.end'?: string;
  'leaseEnd.start'?: string;
  'leaseEnd.end'?: string;
  'moveOutDate.start'?: string;
  'moveOutDate.end'?: string;
  'created.start'?: string;
  'created.end'?: string;
  // Balance filters
  'balance.from'?: number;
  'balance.to'?: number;
  'balance.includeFrom'?: boolean;
  'balance.includeTo'?: boolean;
  'postedBalance.from'?: number;
  'postedBalance.to'?: number;
  'postedBalance.includeFrom'?: boolean;
  'postedBalance.includeTo'?: boolean;
  // Search
  search?: string;
  // Display fields
  displayCustomFieldIds?: string[];
  // Magic Tags filters - these will be added dynamically
  [key: string]: any;
};

export type OwnerPackageReportType = 'BalanceSheet' | 'CashFlowStatement' | 'IncomeStatement' | 'OwnerStatement';

export type CreateOwnerPackageRequestDto = {
  ownerId: string;
  startDate: string;
  endDate: string;
  includeBillInvoices: boolean;
  reportTypes: OwnerPackageReportType[];
};

export type OwnerPackageDto = {
  id: string;
  ownerId: string;
  startDate: string;
  endDate: string;
  includeBillInvoices: boolean;
  reportTypes: OwnerPackageReportType[];
  fileId: string;
  fileUrl: string;
  created: string;
};

export type OwnerPackagesPaginationDto = {
  items: OwnerPackageDto[];
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;
};

export type GetOwnerPackagesFilter = {
  ownerIds?: string[];
  createdAfter?: string;
  createdBefore?: string;
  'pagination.page'?: number;
  'pagination.pageSize'?: number;
};

export class ReportRepository extends BaseRestRepository {
  public async getPropertyReserveReport(filter: PropertyReserveReportFilter): Promise<PropertyReserveReportDto> {
    const url = urlWithQuery(REPORTS_URL + '/property-reserve', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getGeneralLedgerReport(filter: GeneralLedgerReportFilter): Promise<GeneralLedgerDto> {
    const url = urlWithQuery(ACCOUNTING_REPORTS_URL + '/general-ledger', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getBalanceSheetReport(filter?: CreateBalanceSheetParams): Promise<BalanceSheetDto> {
    const url = urlWithQuery(ACCOUNTING_REPORTS_URL + '/balance-sheet', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getCashFlowStatementReport(filter: CreateCashFlowStatementRequestDto): Promise<CashFlowStatementDto[]> {
    const url = urlWithQuery(ACCOUNTING_REPORTS_URL + '/cash-flow-statement', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  // FIXME - 应该使用具体的类型
  public async getIncomeStatementReport(filter?: IncomeStatementReportFilter): Promise<any> {
    const url = urlWithQuery(ACCOUNTING_REPORTS_URL + '/income-statement', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getOwnerStatementReport(filter: GetOwnerStatementReportParams): Promise<OwnerStatementReportDto> {
    const url = urlWithQuery(ACCOUNTING_REPORTS_URL + `/owner-statement`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getRentPaymentsReport(filter: CreateRentPaymentsReportParams): Promise<RentPaymentsReportDto> {
    const url = urlWithQuery(ACCOUNTING_REPORTS_URL + '/rent-payments', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getRentRollReport(filter?: RentRollReportFilter): Promise<any> {
    const url = urlWithQuery(REPORTS_URL + '/rent-roll', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }
  public async getPropertyDirectoryReport(filter?: { active: boolean }): Promise<any> {
    const url = urlWithQuery(REPORTS_URL + '/property-directory', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getInventoryReport(filter?: { showInactive?: boolean }): Promise<any> {
    const url = urlWithQuery(REPORTS_URL + '/inventory', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getOwnerDirectoryReport(): Promise<any> {
    const url = `${REPORTS_URL}/owner-directory`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getTenantDirectoryReport(filter: TenantDirectoryReportFilter): Promise<TenantDirectoryReportDto> {
    const url = urlWithQuery(REPORTS_URL + '/tenant-directory', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getBankAccountActivityReport(filter: BankAccountActivityReportFilter): Promise<BankAccountActivityReportDto> {
    const { bankAccountId, ...rest } = filter;
    validateId(bankAccountId, 'Bank account');
    const url = urlWithQuery(`${REPORTS_URL}/bank-account-activity/${bankAccountId}`, rest);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getLeaseUnpaidBillsReport(
    filter: {
      leaseId?: string;
    } = {}
  ): Promise<LeaseUnpaidLedgerReportDto> {
    const url = urlWithQuery(REPORTS_URL + '/lease-unpaid-ledger', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getDelinquencyReport(filter: DelinquencyReportFilter): Promise<DelinquencyReportDto> {
    const url = urlWithQuery(REPORTS_URL + '/delinquency', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getRentalInsuranceReport(filter?: RentalInsuranceReportFilter): Promise<any> {
    const url = urlWithQuery(REPORTS_URL + '/rental-insurance', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getAccountsPayableReport(filter: AccountsPayableReportFilter): Promise<AccountsPayableReportDto> {
    const url = urlWithQuery(REPORTS_URL + '/accounts-payable', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getPrepaidRentReport(filter: PrepaidRentReportFilter): Promise<PrepaidRentReportDto> {
    const url = urlWithQuery(REPORTS_URL + '/prepaid-rent', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getLateFeesReport(filter: LateFeesReportFilter): Promise<LateFeesReportDto> {
    const url = urlWithQuery(REPORTS_URL + '/late-fees', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getLeaseMagicTagsReport(filter?: LeaseMagicTagsReportFilter): Promise<LeaseMagicTagsReportDto> {
    const url = urlWithQuery(REPORTS_URL + '/magic-tags/leases', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getOwnerPackages(filter?: GetOwnerPackagesFilter): Promise<OwnerPackagesPaginationDto> {
    const url = urlWithQuery(ACCOUNTING_REPORTS_URL + '/owner-package', filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async createOwnerPackage(request: CreateOwnerPackageRequestDto): Promise<OwnerPackageDto> {
    const response = await this.fetchWithAuth(ACCOUNTING_REPORTS_URL + '/owner-package', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return this.getJsonResponse(response);
  }

  public async deleteOwnerPackage(id: string): Promise<void> {
    await this.fetchWithAuth(ACCOUNTING_REPORTS_URL + `/owner-package/${id}`, { method: 'DELETE' });
  }
}

export const reportRepository = new ReportRepository();
